import { useEffect, useRef, useState } from 'react';
import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import { LuCheck, LuStar, LuUser } from 'react-icons/lu';

import SbrIcon from './SbrIcon';

export type CandidateProfile = {
  title: string;
  points: string[];
};

export const RWF_CANDIDATE_PROFILES: CandidateProfile[] = [
  {
    title: 'Ideal Candidate',
    points: [
      'Hard-to-find skills',
      'Proven track record',
      'Culture add',
      'Interview-ready presentation',
    ],
  },
  {
    title: 'Executive Leader',
    points: [
      'C-suite & VP depth',
      'Transformation experience',
      'Board & stakeholder savvy',
      'Scale-up leadership',
    ],
  },
  {
    title: 'Technical Expert',
    points: [
      'Deep specialization',
      'Modern stack fluency',
      'Architecture & delivery',
      'Security-minded builder',
    ],
  },
  {
    title: 'Revenue Driver',
    points: [
      'Quota-crushing history',
      'Enterprise deal craft',
      'Pipeline discipline',
      'Coachable team multiplier',
    ],
  },
];

const ROTATE_MS = 1800;
const SLIDE_DURATION_S = 0.32;
const CARD_DIM_DURATION_S = 0.16;
const CARD_ACTIVE_DURATION_S = 0.24;

function getCarouselMotionScales() {
  const mobile = window.matchMedia('(max-width: 991.98px)').matches;
  return mobile ? { dim: 0.78, active: 0.94 } : { dim: 0.84, active: 1 };
}

type CandidateCardProps = {
  profile: CandidateProfile;
  isActive: boolean;
};

function CandidateCard({ profile, isActive }: CandidateCardProps) {
  return (
    <article
      className={`rwf-candidate-card${isActive ? ' rwf-candidate-card--active' : ' rwf-candidate-card--dim'}`}
      aria-current={isActive ? 'true' : undefined}
      aria-hidden={!isActive}
    >
      {isActive ? (
        <span className="rwf-candidate-card__badge" aria-hidden>
          <SbrIcon icon={LuCheck} size={16} />
        </span>
      ) : null}
      <div className="rwf-candidate-card__avatar">
        <SbrIcon icon={LuUser} size={isActive ? 34 : 28} />
      </div>
      <div className={`rwf-candidate-card__stars${isActive ? '' : ' rwf-candidate-card__stars--dim'}`}>
        {Array.from({ length: 5 }).map((_, i) => (
          <SbrIcon key={i} icon={LuStar} size={isActive ? 14 : 12} />
        ))}
      </div>
      {isActive ? (
        <>
          <h3 className="rwf-candidate-card__title">{profile.title}</h3>
          <ul className="rwf-candidate-card__checks">
            {profile.points.map((item) => (
              <li key={item}>
                <SbrIcon icon={LuCheck} size={14} className="rwf-candidate-card__check-icon" aria-hidden />
                {item}
              </li>
            ))}
          </ul>
        </>
      ) : (
        <>
          <div className="rwf-candidate-card__title rwf-candidate-card__title--placeholder" />
          <div className="rwf-candidate-card__lines">
            <span />
            <span />
            <span />
            <span />
          </div>
        </>
      )}
    </article>
  );
}

export default function RolesWeFillCandidateCarousel() {
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const centerTrack = () => {
    const viewport = viewportRef.current;
    const track = trackRef.current;
    if (!viewport || !track) return;

    const card = track.children[activeIndex] as HTMLElement | undefined;
    if (!card) return;

    const x = viewport.clientWidth / 2 - (card.offsetLeft + card.offsetWidth / 2);
    gsap.to(track, { x, duration: SLIDE_DURATION_S, ease: 'power3.inOut', overwrite: true });
  };

  useEffect(() => {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion) return;

    const id = window.setInterval(() => {
      setActiveIndex((i) => (i + 1) % RWF_CANDIDATE_PROFILES.length);
    }, ROTATE_MS);

    return () => window.clearInterval(id);
  }, []);

  useGSAP(
    () => {
      const track = trackRef.current;
      if (!track) return;

      centerTrack();

      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reducedMotion) return;

      const { dim: dimScale, active: activeScale } = getCarouselMotionScales();
      const cards = gsap.utils.toArray<HTMLElement>(track.children);
      gsap.to(cards, {
        scale: dimScale,
        opacity: 0.38,
        duration: CARD_DIM_DURATION_S,
        ease: 'power2.out',
        overwrite: true,
      });

      const active = cards[activeIndex];
      if (active) {
        gsap.to(active, {
          scale: activeScale,
          opacity: 1,
          duration: CARD_ACTIVE_DURATION_S,
          ease: 'back.out(1.2)',
          overwrite: true,
        });
      }
    },
    { dependencies: [activeIndex] },
  );

  useGSAP(
    () => {
      const onResize = () => centerTrack();
      centerTrack();
      window.addEventListener('resize', onResize);
      return () => window.removeEventListener('resize', onResize);
    },
    { scope: viewportRef },
  );

  return (
    <div className="rwf-candidate-carousel w-100" aria-roledescription="carousel" aria-label="Candidate profiles">
      <div ref={viewportRef} className="rwf-candidate-carousel__viewport">
        <div ref={trackRef} className="rwf-candidate-carousel__track">
          {RWF_CANDIDATE_PROFILES.map((profile, index) => (
            <CandidateCard key={profile.title} profile={profile} isActive={index === activeIndex} />
          ))}
        </div>
      </div>
    </div>
  );
}
